<script setup lang="ts">
import { ref } from 'vue';

type Currency = {
  code: string;
  symbol: string;
  name: string;
  color: string;
};

const currencies: Currency[] = [
  { code: 'EUR', symbol: '€', name: 'Euro', color: '#003399' },
  { code: 'USD', symbol: '$', name: 'US Dollar', color: '#6CDE07' },
  { code: 'GBP', symbol: '£', name: 'British Pound', color: '#BC3FE0' }
];

const selectedCurrency = ref<Currency>(currencies[0]);
const isOpen = ref(false);

const toggleDropdown = () => {
  isOpen.value = !isOpen.value;
};

const selectCurrency = (currency: Currency) => {
  selectedCurrency.value = currency;
  isOpen.value = false;
};
</script>

<template>
  <div class="relative w-full md:w-[345px]">
    <button
      @click="toggleDropdown"
      class="px-4 py-2 bg-[var(--m-blue-1)] text-[var(--m-primary-base)] rounded-lg justify-between items-center gap-3 inline-flex hover:bg-[var(--m-blue-2)] active:bg-[var(--m-blue-3)] transition-colors w-full h-[54px] font-medium"
    >
      <div class="flex items-center gap-2">
        <div
          class="w-[38px] h-[38px] rounded-full flex justify-center items-center text-white font-bold text-xl"
          :style="{ backgroundColor: selectedCurrency.color }"
        >
          {{ selectedCurrency.symbol }}
        </div>
        <div class="text-lg/[38px] font-medium font-['Poppins']">
          {{ selectedCurrency.code }}
        </div>
      </div>
      <svg
        class="w-6 h-6 transition-transform"
        :class="{ 'rotate-180': isOpen }"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
      </svg>
    </button>

    <!-- Dropdown Menu -->
    <div
      v-if="isOpen"
      class="absolute top-full mt-2 left-0 w-full bg-white rounded-lg shadow-lg border border-gray-200 z-50"
    >
      <button
        v-for="currency in currencies"
        :key="currency.code"
        @click="selectCurrency(currency)"
        class="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 transition-colors first:rounded-t-lg last:rounded-b-lg"
        :class="{ 'bg-gray-100': selectedCurrency.code === currency.code }"
      >
        <div
          class="w-[32px] h-[32px] rounded-full flex justify-center items-center text-white font-bold text-lg"
          :style="{ backgroundColor: currency.color }"
        >
          {{ currency.symbol }}
        </div>
        <div class="flex flex-col items-start">
          <div class="text-zinc-800 font-medium">{{ currency.code }}</div>
          <div class="text-gray-500 text-sm">{{ currency.name }}</div>
        </div>
      </button>
    </div>
  </div>
</template>

<style scoped>
.rotate-180 {
  transform: rotate(180deg);
}
</style>
